"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  hasAcceptedAnalyticsConsent,
  persistAnalyticsConsent,
  readAnalyticsConsent,
  type AnalyticsConsentChoice,
  ANALYTICS_CONSENT_EVENT,
} from "./analyticsConsent";

type AnalyticsConsentContextValue = {
  choice: AnalyticsConsentChoice | null;
  ready: boolean;
  accepted: boolean;
  setChoice: (choice: AnalyticsConsentChoice) => void;
};

const AnalyticsConsentContext = createContext<AnalyticsConsentContextValue | null>(null);

/** Provides the stored analytics preference to GTM, the Event beacon, and the banner. */
export function AnalyticsConsentProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<AnalyticsConsentChoice | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setChoiceState(readAnalyticsConsent());
    setReady(true);
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<AnalyticsConsentChoice>).detail;
      if (detail === "accepted" || detail === "necessary") {
        setChoiceState(detail);
      } else {
        setChoiceState(readAnalyticsConsent());
      }
    };
    window.addEventListener(ANALYTICS_CONSENT_EVENT, onChange);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, onChange);
  }, []);

  const setChoice = useCallback((next: AnalyticsConsentChoice) => {
    persistAnalyticsConsent(next);
    setChoiceState(next);
  }, []);

  const value = useMemo<AnalyticsConsentContextValue>(
    () => ({
      choice,
      ready,
      accepted: choice === "accepted",
      setChoice,
    }),
    [choice, ready, setChoice],
  );

  return <AnalyticsConsentContext.Provider value={value}>{children}</AnalyticsConsentContext.Provider>;
}

export function useAnalyticsConsent(): AnalyticsConsentContextValue {
  const ctx = useContext(AnalyticsConsentContext);
  if (ctx) return ctx;
  const stored = typeof window === "undefined" ? null : readAnalyticsConsent();
  return {
    choice: stored,
    ready: typeof window !== "undefined",
    accepted: hasAcceptedAnalyticsConsent(),
    setChoice: persistAnalyticsConsent,
  };
}
